const normalizeComparableText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[’']/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const LABEL_STOP_WORDS = new Set([
  'a',
  'au',
  'aux',
  'de',
  'des',
  'du',
  'et',
  'la',
  'le',
  'les',
  'un',
  'une',
]);

const INSTRUCTION_QUALIFIERS = new Set([
  'utile',
  'tres',
  'court',
  'courte',
  'concis',
  'concise',
  'structure',
  'structuree',
  'clair',
  'claire',
  'complet',
  'complete',
  'detaille',
  'detaillee',
  'bref',
  'breve',
  'actionnable',
]);

const stripInstructionQualifiers = (value: string): string =>
  value
    .split(/\s+/)
    .filter((token) => !INSTRUCTION_QUALIFIERS.has(normalizeComparableText(token)))
    .join(' ')
    .replace(/\s+/g, ' ')
    .replace(/\b(?:et|ou)\b\s*$/iu, '')
    .trim();

export const sweepVisibleHeading = (value: string): string => {
  const trimmed = value.trim();
  const comparable = normalizeComparableText(trimmed);

  if (comparable === 'elements reutilisables dans l account thread') return 'Repères du compte';
  if (comparable === 'artefact') return 'Document';
  if (comparable === 'high priority actions') return 'Actions prioritaires';
  if (comparable === 'follow up pressure') return 'Relance nécessaire';
  if (comparable === 'what happened useful context') return 'Ce qui a changé et contexte utile';
  if (/^(synthese|resume)\b/.test(comparable)) {
    return stripInstructionQualifiers(trimmed).replace(/[,:;-]+\s*$/u, '') || 'Synthèse';
  }

  return trimmed;
};

const replaceVisibleTerm = (match: string, singular: string, plural: string): string => {
  const replacement = /s$/i.test(match) ? plural : singular;
  return /^[A-ZÀ-Þ]/u.test(match)
    ? `${replacement.charAt(0).toUpperCase()}${replacement.slice(1)}`
    : replacement;
};

export const sweepBusinessVocabulary = (value: string): string =>
  value
    .replace(/\bnotes? de calls?\b/giu, (match) => {
      const replacement = match.toLowerCase().startsWith('note ') ? 'note d’appel' : 'notes d’appel';
      return /^[A-ZÀ-Þ]/u.test(match) ? `${replacement.charAt(0).toUpperCase()}${replacement.slice(1)}` : replacement;
    })
    .replace(/\banalyse de call\b/giu, (match) => /^[A-ZÀ-Þ]/u.test(match) ? 'Analyse d’appel' : 'analyse d’appel')
    .replace(/\bsynth[eè]se call\b/giu, (match) => /^[A-ZÀ-Þ]/u.test(match) ? 'Synthèse d’appel' : 'synthèse d’appel')
    .replace(/\ble (?:business case|argumentaire économique)\b/giu, 'l’argumentaire économique')
    .replace(/\bbusiness case\b/giu, 'argumentaire économique')
    .replace(/\bun transcripts?\b/giu, 'une transcription')
    .replace(/\bla team\b/giu, 'l’équipe')
    .replace(/\bcalls?\b/giu, (match) => replaceVisibleTerm(match, 'appel', 'appels'))
    .replace(/\btranscripts?\b/giu, (match) => replaceVisibleTerm(match, 'transcription', 'transcriptions'))
    .replace(/\bfollow-ups?\b/giu, (match) => replaceVisibleTerm(match, 'suivi', 'suivis'))
    .replace(/\bdrafts?\b/giu, (match) => replaceVisibleTerm(match, 'brouillon', 'brouillons'))
    .replace(/\bmeetings?\b/giu, (match) => replaceVisibleTerm(match, 'rendez-vous', 'rendez-vous'))
    .replace(/\bdeals?\b/giu, (match) => replaceVisibleTerm(match, 'opportunité', 'opportunités'))
    .replace(/\btools?\b/giu, (match) => replaceVisibleTerm(match, 'application', 'applications'))
    .replace(/\binputs?\b/giu, (match) => replaceVisibleTerm(match, 'information', 'informations'))
    .replace(/\bteams?\b/giu, (match) => replaceVisibleTerm(match, 'équipe', 'équipes'))
    .replace(/\bflows?\b/giu, (match) => replaceVisibleTerm(match, 'processus', 'processus'));

export const sweepMarkdownHeadings = (markdown: string): string =>
  markdown
    .split(/\r?\n/)
    .map((line) => {
      const match = /^(#{1,6}\s+)(.+?)\s*$/.exec(line);
      if (!match) return line;
      const prefix = match[1];
      const heading = match[2];
      if (!prefix || !heading) return line;
      return `${prefix}${sweepVisibleHeading(heading)}`;
    })
    .join('\n');

const meaningfulTokens = (value: string): string[] =>
  normalizeComparableText(value)
    .split(' ')
    .filter((token) => token.length > 1 && !LABEL_STOP_WORDS.has(token));

export const isRedundantVisibleLabel = (
  primaryLabel: string,
  candidateLabel: string
): boolean => {
  const primary = normalizeComparableText(primaryLabel);
  const candidate = normalizeComparableText(candidateLabel);
  if (!primary || !candidate) return false;
  if (primary === candidate) return true;

  const primaryTokens = new Set(meaningfulTokens(primaryLabel));
  const candidateTokens = meaningfulTokens(candidateLabel);
  return (
    candidateTokens.length >= 2 &&
    candidateTokens.every((token) => primaryTokens.has(token))
  );
};

export const getResultTabLabel = (toolTitle: string, tabLabel: string): string =>
  isRedundantVisibleLabel(toolTitle, tabLabel)
    ? 'Résultat'
    : sweepVisibleHeading(tabLabel);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const cleanMarkdownParagraph = (value: string): string =>
  value
    .replace(/^[-*+]\s+/gm, '')
    .replace(/[*_`>#]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

const extractMarkdownSummary = (markdown: string): string | null => {
  const lines = markdown.split(/\r?\n/);
  let collecting = false;
  const collected: string[] = [];

  for (const line of lines) {
    const heading = /^#{1,6}\s+(.+?)\s*$/.exec(line)?.[1];
    if (heading) {
      if (collecting) break;
      collecting = /^(synthese|resume|verdict)\b/.test(
        normalizeComparableText(heading)
      );
      continue;
    }
    if (collecting && line.trim()) collected.push(line.trim());
  }

  const summary = cleanMarkdownParagraph(collected.join(' '));
  return summary || null;
};

const extractBusinessSummary = (value: unknown, depth = 0): string | null => {
  if (!isRecord(value) || depth > 3) return null;

  const artifact = isRecord(value.artifact_document) ? value.artifact_document : null;
  if (artifact && typeof artifact.body === 'string') {
    const summary = extractMarkdownSummary(artifact.body);
    if (summary) return summary;
  }

  for (const key of [
    'short_summary',
    'summary',
    'synthesis',
    'synthese',
    'outcome_summary',
    'narration',
  ]) {
    const candidate = value[key];
    if (typeof candidate === 'string' && candidate.trim()) {
      return cleanMarkdownParagraph(candidate);
    }
  }

  for (const candidate of Object.values(value)) {
    const summary = extractBusinessSummary(candidate, depth + 1);
    if (summary) return summary;
  }

  return null;
};

const extractTruncatedSerializedSummary = (value: string): string | null => {
  const narrationFragment = /["']narration["']\s*:\s*["']((?:\\.|[^"'])*)/u.exec(value)?.[1];
  if (narrationFragment) {
    const decoded = narrationFragment
      .replace(/\\n/g, ' ')
      .replace(/\\r/g, ' ')
      .replace(/\\t/g, ' ')
      .replace(/\\"/g, '"')
      .replace(/\\\\/g, '\\')
      .replace(/\s+/g, ' ')
      .trim();
    if (decoded) return /[.!?…]$/u.test(decoded) ? decoded : `${decoded}…`;
  }
  const bodyFragment = /["']body["']\s*:\s*["']([\s\S]*)$/u.exec(value)?.[1];
  if (!bodyFragment) return null;

  const decodedFragment = bodyFragment
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\n')
    .replace(/\\t/g, ' ')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');
  const summary = extractMarkdownSummary(decodedFragment);
  if (!summary) return null;
  return /[.!?…]$/u.test(summary) ? summary : `${summary}…`;
};

export const sweepSerializedBusinessText = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return trimmed;

  try {
    return extractBusinessSummary(JSON.parse(trimmed)) ?? trimmed;
  } catch {
    return extractTruncatedSerializedSummary(trimmed) ?? trimmed;
  }
};

export const sweepActivitySummary = (
  value: string | null | undefined,
  failed = false,
): string | null => {
  if (!value?.trim()) return failed ? 'La préparation n’a pas abouti.' : null;
  const swept = sweepSerializedBusinessText(value).trim();
  if (
    !swept ||
    swept.startsWith('{') ||
    swept.startsWith('[') ||
    /\b(?:api key not configured|api error|http\s*[45]\d\d|stack trace|exception)\b/i.test(swept)
  ) {
    return failed
      ? 'La préparation n’a pas abouti. Ouvrez le compte pour la reprendre.'
      : 'Le détail de cet ancien résultat est disponible dans le compte.';
  }
  return swept;
};

export const isInternalMechanicsCopy = (value: string): boolean =>
  /\b(?:artefact|primitive|payload|renderer|tool interne|pause hitl|jobKey|workIntent|desiredOutcome|capability|ex[eé]cuteur|depuis cette projection|ex[eé]cuteur fiable|catalogue disponible|system\.[a-z_]+)\b/iu.test(
    value
  );

/** Display-only filtering; never changes a stored recommendation or its action. */
export const businessSupportingText = (value: string | null | undefined, ...visible: string[]): string | null => {
  if (!value) return null;
  const text = sweepSerializedBusinessText(value);
  if (/^(open|pending|ready|running|completed|failed|unavailable)$/i.test(text) || isInternalMechanicsCopy(text)) return null;
  return visible.some((label) => isRedundantVisibleLabel(label, text)) ? null : text;
};
